import { Injectable, signal, computed } from '@angular/core';

@Injectable({
  providedIn: 'root',
})

export class CommonService {

  constructor() {
    this.captureEntryUrl();
  }

  isPanelOpen = signal(false);
  activeAgentId = signal<number | null>(null);
  activeAgentContext = signal<any>(null);
    showMobileNav = signal(true);
  showMobileFloatingBar = signal(true);
  forceHideMobileNav = signal(false);
  forceHideMobileFloatingBar = signal(false);

  activeDetailedView = signal<string | null>(null);
  viewAllSections = signal<string[]>([
    'activeBusinesses',
    'topBusinesses',
    'topCategories',
    'overallContracts',
    'enrolledBusinesses',
    'contractsCreated',
    'followersByPlatform'
  ]);

  isDetailedViewActive = computed(() => {
    const active = this.activeDetailedView();
    return active ? this.viewAllSections().includes(active) : false;
  });
  aiSuggestions = signal<string[]>([]);
  chatType = signal<'MONTHLY_REPORT_1' | 'CALL_AUDIT_HISTORY' | 'TRAINING_ANALYTICS' | 'ARRANGMENT_CRON' | 'CRON_PAYMENTS' | 'DAILY_CRON_PAYMENT' | 'SEO_STATS' | 'CAMPAIGN_METRICS' | 'SEO_OVERVIEW_GOOGLE' | 'SEO_OVERVIEW_META' | 'ADS_OVERVIEW' | 'INDUSTRY_CATEGORIZATION' | 'SOCIAL_MEDIA_PERFORMNACE'>('MONTHLY_REPORT_1');

  // Persistent Chat State Management

  activeChatContextKey = signal<string | null>(null);
  private _chatStates = new Map<string, any>();

  saveChatState(key: string, state: any) {
    if (!key) return;
    this._chatStates.set(key, state);
  }

  getChatState(key: string) {
    return this._chatStates.get(key);
  }

  clearAllChatStates() {
    this._chatStates.clear();
    this.activeChatContextKey.set(null);
  }

  togglePanel() {
    this.isPanelOpen.set(!this.isPanelOpen());
  }

  updateBodyPanelCount(delta: number) {
    if (typeof document === 'undefined') return;
    const body = document.body;
    const current = Number(body.getAttribute('data-open-asides') || '0');
    const next = Math.max(0, current + delta);
    body.setAttribute('data-open-asides', String(next));
  }

  // ── Agent-resolve race-condition guard ───────────────────────────────────
  // When a resolveAgent request is in-flight this holds a Promise that settles
  // once the HTTP response arrives.  sendQuery awaits it before reading agentId.
  agentResolvePending: Promise<number | null> | null = null;
  private _agentResolveFn: ((id: number | null) => void) | null = null;

  /** Call just before firing a resolveAgent HTTP request. */
  startAgentResolve(): void {
    // Settle any previous dangling promise so no caller hangs forever.
    if (this._agentResolveFn) {
      this._agentResolveFn(this.activeAgentId());
    }
    this.agentResolvePending = new Promise<number | null>(resolve => {
      this._agentResolveFn = resolve;
    });
  }

  /** Call when the resolveAgent response arrives (success or error). */
  completeAgentResolve(agentId: number | null): void {
    this.activeAgentId.set(agentId);
    if (this._agentResolveFn) {
      this._agentResolveFn(agentId);
      this._agentResolveFn = null;
    }
    this.agentResolvePending = null;
  }

  // ── Post-login redirect ──────────────────────────────────────────────────
  // Remembers the in-app URL the user was trying to reach when the session was
  // missing or expired, so login can send them back there instead of dumping
  // them on /app.
  //
  // Held in a signal rather than sessionStorage because AuthService.clearUser()
  // wipes sessionStorage on every sign-out and 401 — exactly when the URL needs
  // to survive. Everything from the failed navigation to the successful login
  // is one SPA session, so in-memory state is enough.

  private readonly _redirectUrl = signal<string | null>(null);

  /** The pending post-login destination, or null when there isn't one. */
  readonly redirectUrl = this._redirectUrl.asReadonly();

  /**
   * Records the URL the user actually asked for, straight from the address
   * bar. This has to happen before the router's first navigation: dynamic
   * product routes live in sessionStorage, so on a fresh tab they don't exist
   * yet and a deep link like /app/credee/tracking-demo matches nothing. The
   * router then resolves it through the `'' -> denefits` redirect, and by the
   * time a guard reads `state.url` the real destination is already gone.
   */
  private captureEntryUrl(): void {
    if (typeof window === 'undefined') return;
    this.captureRedirectUrl(window.location.pathname + window.location.search);
  }

  /**
   * Remembers `url` as the post-login destination, if it is a real in-app
   * route. Anything else (a public route, an absolute URL, /app itself) is
   * ignored, so callers can pass `router.url` blindly.
   *
   * Pass `overwrite: false` when the URL is a router-derived guess (post
   * redirect resolution) that must not clobber the entry URL captured above.
   */
  captureRedirectUrl(url: string | null | undefined, overwrite = true): void {
    if (!overwrite && this._redirectUrl() !== null) {
      return;
    }
    const candidate = this.normalizeRedirectUrl(url);
    if (candidate) {
      this._redirectUrl.set(candidate);
    }
  }

  /**
   * Returns the pending destination and clears it, so it is used exactly once.
   * `fallbackUrl` (e.g. a `returnUrl` query param, which survives a page reload
   * where the signal does not) is sanitized the same way before use.
   */
  consumeRedirectUrl(fallbackUrl?: string | null): string {
    const stored = this._redirectUrl();
    this._redirectUrl.set(null);
    return stored ?? this.normalizeRedirectUrl(fallbackUrl) ?? '/app';
  }

  /** Drops any pending destination — used on a deliberate sign-out. */
  clearRedirectUrl(): void {
    this._redirectUrl.set(null);
  }

  /**
   * Accepts only same-origin app paths worth returning to. Rejecting anything
   * that isn't a single-slash-rooted path also keeps a crafted
   * `?returnUrl=//evil.com` out of navigateByUrl.
   */
  private normalizeRedirectUrl(url: string | null | undefined): string | null {
    if (!url) return null;

    const trimmed = url.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;

    const path = trimmed.split(/[?#]/)[0].replace(/\/+$/, '');
    if (!path || path === '/app') return null;

    // Public routes would loop the user straight back to where they started.
    const isPublic = [
      '/login',
      '/verify',
      '/password-setup',
      '/forgot-password',
      '/reset-password',
    ].some(prefix => path === prefix || path.startsWith(`${prefix}/`));

    return isPublic ? null : trimmed;
  }

}
